import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Phone } from 'lucide-react';

interface CredentialsStepProps {
  initialPhone: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onEdit: () => void;
  onSubmit: (phone: string) => void;
}

const PHONE_REGEX = /^05\d{8}$/;

const CredentialsStep: React.FC<CredentialsStepProps> = ({
  initialPhone,
  isSubmitting,
  errorMessage,
  onEdit,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const [phone, setPhone] = useState(initialPhone);
  const [touched, setTouched] = useState(false);

  const phoneIsValid = PHONE_REGEX.test(phone.trim());
  const canSubmit = phoneIsValid && !isSubmitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit(phone.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
          {t('courierAuth.credentials.heading')}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          {t('courierAuth.credentials.subheading')}
        </p>
      </div>

      <div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700">
            {t('courierAuth.credentials.phoneLabel')}
          </label>
          <div className="relative" dir="ltr">
            <span className="absolute inset-y-0 start-3 flex items-center text-gray-400 pointer-events-none">
              <Phone className="h-4 w-4" />
            </span>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              dir="ltr"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                onEdit();
              }}
              placeholder="0501234567"
              className={`w-full rounded-xl border bg-white py-3 pe-3 ps-10 outline-none transition-colors focus:ring-2 focus:ring-offset-1 ${
                touched && !phoneIsValid
                  ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-200'
                  : 'border-gray-300 focus:border-[#bf0035] focus:ring-[#bf0035]/15'
              }`}
            />
          </div>
          {touched && !phoneIsValid && (
            <p className="text-xs text-red-600 mt-1.5">
              {t('courierAuth.credentials.phoneError')}
            </p>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="border-s-2 border-red-600 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="btn-primary flex w-full items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          t('courierAuth.credentials.sendOtp')
        )}
      </button>
    </form>
  );
};

export default CredentialsStep;
